import DOM from './dom';
import assign from 'assign-deep';


class Underscore {
    isArguments(input) : boolean {
        return '[object Arguments]' === Object.prototype.toString.call(input);
    }

    isFunction(input) : boolean {
        return '[object Function]' === Object.prototype.toString.call(input);
    }

    isString(input) : boolean {
        return '[object String]' === Object.prototype.toString.call(input);
    }

    isNumber(input) : boolean {
        return '[object Number]' === Object.prototype.toString.call(input) && !isNaN(input);
    }

    isObject(input) : boolean {
        return '[object Object]' === Object.prototype.toString.call(input);
    }

    isDate(input) : boolean {
        return '[object Date]' === Object.prototype.toString.call(input);
    }

    isArray(expectedArray) : boolean {
        return Array.isArray(expectedArray);
    }

    isBoolean(input) : boolean {
        return true === input || false === input || '[object Boolean]' === Object.prototype.toString.call(input);
    }


    isError(input) : boolean {
        return '[object Error]' === Object.prototype.toString.call(input);
    }

    isSymbol(input) : boolean {
        return '[object Symbol]' === Object.prototype.toString.call(input);
    }

    isUndefined(input) : boolean {
        return '[object Undefined]' === Object.prototype.toString.call(input);
    }

    isHTMLDivElement(input) : boolean {
        return '[object HTMLDivElement]' === Object.prototype.toString.call(input);
    }

    isHTMLImgElement(input) : boolean {
        return '[object HTMLImageElement]' === Object.prototype.toString.call(input);
    }


    isNull(value) : boolean {
        return 'null' === value || value === null;
    }

    isNullOrUndefined(input) : boolean {
        return this.isNull(input) || this.isUndefined(input);
    }

	propertyChainExists(obj: any, propChain: string) : boolean {
        let properties = propChain.split('.');
        while (properties.length > 0) {
            obj = obj[properties.shift()];
            if (this.isUndefined(obj)) {
                return false;
            }
        }
        return true;
    }

    isEmpty(input) : boolean {

        return this.isNullOrUndefined(input) || 0 === input.length || (input.constructor === Object && Object.keys(input).length === 0);
    }


    safeStringToNumber(str: string, defaultValue?: number) : number {

        if(this.isNullOrUndefined(str)) {
            if(this.isNullOrUndefined(defaultValue)) {
                throw new Error(`str parameter is null or undefined and no default value provided`);
            }

            return defaultValue;
        }

        let result = Number(str);

        if (Number.isNaN(result)) {
            if(this.isNullOrUndefined(defaultValue)) {
                throw new Error(`${str} is not a number and no default value provided`);
            }
            return defaultValue;
        }

        return result;
    }


    isNodeList(expectedNodeList: any) : boolean {
        var stringRepr = Object.prototype.toString.call(expectedNodeList);

        return typeof expectedNodeList === 'object' &&
            /^\[object (HTMLCollection|NodeList|Object)\]$/.test(stringRepr) &&
            (typeof expectedNodeList.length === 'number') &&
            (expectedNodeList.length === 0 || (typeof expectedNodeList[0] === "object" && expectedNodeList[0].nodeType > 0));
    }


    isElement(expectedElement: any) : boolean {
        return expectedElement instanceof HTMLElement;
    }

    /**
     * Check if string includes another substring
     *
     */
    includes(str: string, substr: string) : boolean{
        return str.indexOf(substr) !== -1;
    }


    /**
     * Load async js script code.
     *
     */
    loadAsyncScript(src: string, id: string = 'cgp-dynamic-script-' + Date.now(), type: string = 'text/javascript') : Promise<any> {
        return new Promise<any>((resolve, reject) => {
            var s;
            s = document.createElement('script');
            s.src = src;
            s.id = id;
            s.type = type;
            s.onload = resolve;
            s.onerror = reject;
            document.body.appendChild(s);
        });
    }


    /**
     * Generate GUID
     */
    getGuid(): string {
        let s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };

        return s4() + s4()  + s4()  + s4() +
            s4()  + s4() + s4() + s4();
    }


    /**
     * Make id.
     *
     */
    makeid() : string {
        var text = "";
        var possible = "abcdefghijklmnopqrstuvwxyz";

        for (var i = 0; i < 10; i++)
            text += possible.charAt(Math.floor(Math.random() * possible.length));

        return text;
    }

    /**
     * return if the link is absolute
     * @param link
     * @returns {boolean}
     */
    isAbsoluteLink(link) {
        let regex = /^https?:\/\//i;
        return regex.test(link);
    }

    /**
     * Get hostname.
     *
     */
    getHostName(url: string) : string {
        const match = url.match(/(https?:\/\/[^/?#]+)/i);
        if (match != null && match.length === 2 && typeof match[1] === 'string' && match[1].length > 0) {
            return match[1];
        }
        else {
            return null;
        }
    }


    /**
     * Determinate if the given URL is valid(support also mailto url)
     *
     */
    isValidUrl(url: any): boolean {
      if (!this.isString(url))
        return false;
      var rg = /(^(((https?):\/\/)|(\/))[^\s]+$)|((^(mailto:))[^\s]+@[^\s]+$)/;
      return rg.test(url);
    }

    isAbsolutUrl(url: string): boolean {
        try {
            new URL(url);
        }catch (err) {
            return false;
        }

        return true;
    }

    /**
     * toggle option, kicks into and out of fullscreen mode.
     */
    toggleFullScreen(): void {
        if (this.isFullScreen()) {
            this.closeFullScreen();
        } else {
            this.openFullScreen();
        }
    }

    openFullScreen(): void {
        if (document.documentElement['requestFullScreen']) {
            document.documentElement['requestFullScreen']();
        } else if (document.documentElement['mozRequestFullScreen']) {
            document.documentElement['mozRequestFullScreen']();
        } else if (document.documentElement['webkitRequestFullScreen']) {
            document.documentElement['webkitRequestFullScreen'](Element['ALLOW_KEYBOARD_INPUT']);
        } else if (document.documentElement['msRequestFullscreen']) {
            if (!DOM.hasClass(document.body, 'cgp-ie-fullscreen')) {
                DOM.addClass(document.body, 'cgp-ie-fullscreen');
            }
            document.documentElement['msRequestFullscreen']();
        }

    }

    closeFullScreen(): void {
        if (document['cancelFullScreen']) {
            document['cancelFullScreen']();
        } else if (document['mozCancelFullScreen']) {
            document['mozCancelFullScreen']();
        } else if (document['webkitCancelFullScreen']) {
            document['webkitCancelFullScreen']();
        } else if (document['msExitFullscreen']) {
            DOM.removeClass(document.body, 'cgp-ie-fullscreen');
            document['msExitFullscreen']();
        }
    }

    isFullScreen() : boolean {
        return !this.isNullOrUndefined(document['mozFullScreenElement'])
                || !this.isNullOrUndefined(document['webkitFullscreenElement'])
                || !this.isNullOrUndefined(document['webkitCurrentFullScreenElement'])
                || !this.isNullOrUndefined(document['msFullscreenElement'])
                || !this.isNullOrUndefined(document['fullscreenElement']);
    }


    /**
     * If the user is caps lock.
     */
      isCapslock(event: any) : boolean {
        event = (event) ? event : window.event;

        let charCode: number = null;
        if (event.which) {
            charCode = event.which;
        } else if (event.keyCode) {
            charCode = event.keyCode;
        }

        let shiftOn = false;
        if (event.shiftKey) {
            shiftOn = event.shiftKey;
        } else if (event.modifiers) {
            shiftOn = !!(event.modifiers & 4);
        }

        if (charCode >= 97 && charCode <= 122 && shiftOn) {
            return true;
        }

        if (charCode >= 65 && charCode <= 90 && !shiftOn) {
            return true;
        }

        return false;
    }

    /**
     * Merge/Extend Object
     *
     */
    mergeDeep(target: any, ...source) : any  {
        source.forEach(curr => {
            assign(target, curr);
        });

        return target;
    }

    _createXmlDoc(xmlStr: string) {
        const xmlDoc = new (window as any).ActiveXObject("Microsoft.XMLDOM");
        xmlDoc.async = "false";
        xmlDoc.loadXML(xmlStr);
        return xmlDoc;
    }

    parseXml(xmlStr: string)
    {
        try {
            if ((window as any).DOMParser) {
                return new DOMParser().parseFromString(xmlStr, "text/xml");
            } else if ((window as any).ActiveXObject) {
                return this._createXmlDoc(xmlStr);
            }
        }
        catch(err) {
            return null;
        }
        if ((window as any).DOMParser) {
            try {
                return new DOMParser().parseFromString(xmlStr, "text/xml");
            }
            catch(err) {
                return null;
            }
        } else if ((window as any).ActiveXObject) {
            try {
                return this._createXmlDoc(xmlStr);
            }
            catch(err) {
                return null;
            }
        }
        else
            return null;
    }

    /**
     * Get Query String or "string" as Object
     *
     */
    getQueryParameters() : any {
        let pairs = window.location.search.slice(1).split('&');
        let result = {};

        pairs.forEach(pair => {
            try {
                let pairMatch = pair.match(/([^?=]+)=(([^&]*))/).slice(1);
                result[pairMatch[0]] = decodeURIComponent(pairMatch[1] || '');
            } catch(e) {
                //failed to parse QS
            }
        });

        return JSON.parse(JSON.stringify(result));
    }

    jsonToQueryString(json: any): string {
        return this.isEmpty(json) ? '' : '?' +
            Object.keys(json).map(function(key) {
                return encodeURIComponent(key) + '=' +
                    encodeURIComponent(json[key]);
            }).join('&');
    }


    /**
     * Add stop scrolling class to body.
     *
     */
    stopScrolling() {
        DOM.addClass(DOM.getBody(), 'stop-scrolling');
    }


    /**
     * Safe way to parse json stringfy.
     *
     * @param toParse - The string we want to parse to json.
     * @param defaultInException - The default value to return when exception thrown.
     * @returns {undefined}
     */
    safeJSON(toParse: string, defaultInException?: any) : any {
        try {
            return JSON.parse(toParse);
        } catch (ex) {
            return defaultInException || ex;
        }
    }

    safeJSONandDecode(input: string): object{
        try {
            const json = decodeURIComponent(input);
            return JSON.parse(json);
        } catch(e) {
        }
        return {};
    }

    getJSONVal(json: object, key: string): any {

        let val;
        try {
            val = json[key];
        } catch (e) {
        }
        return (undefined !== val) ? val : '';
    }


    dasherize(str: string): string {
        return (str[0].toLowerCase() + str.slice(1)).trim().replace(/[_\s]+/g, '-').replace(/([A-Z])/g, '-$1').replace(/-+/g, '-').toLowerCase();
    }


    /**
     * Parse string function.
     */
    parseStringFunction(strFunction: string) : {rawStringFunction: string, functionName: string, functionStringParams:string, functionArrayParams: Array<string>} {
        const START = 0;

        let startFunctionParams = strFunction.indexOf('('),
            endFunctionParams = strFunction.indexOf(')');

        let strFunctionName = strFunction.substring(START, startFunctionParams),
            strFunctionParams = strFunction.substring(startFunctionParams + 1, endFunctionParams),
            arrayFunctionParams = strFunctionParams.replace(/\s+/g, '').split(',');

        return {
            rawStringFunction: strFunction,
            functionName: strFunctionName,
            functionStringParams: strFunctionParams,
            functionArrayParams: arrayFunctionParams
        }
    }


    /**
     * Returns the object without nullable or undefined parameters
     */
    cleanObject(obj: any, options: any) : any {
        options = Object.assign({removeUndefined: true, removeNull: false}, options);
        var cleanObject = {};
        Object.entries(obj).forEach(([key, value]) => {
            if ((undefined === value && true === options.removeUndefined) || (null === value && true === options.removeNull)) {
                return;
            } else {
                cleanObject[key] = value;
            }
        });
        return cleanObject;
    }


    /**
     * Generic convert to boolean
     */
    toBoolean(value) {
        if (this.isString(value) && (value === 'false' || value === 'true')) {
            return value === 'true';
        }
        return Boolean(value);
    }


    /**
     * Scroll with smooth animation
     * @param direction
     * @param scrollSize
     * @param container
     * @param duration
     * @param smooothLevel
     * @private
     */
    smoothScrolling(direction: string, scrollSize: number, duration: number, container: HTMLElement, smoothLevel: number = 20): Promise<any> {
        return new Promise<void>((res) => {
            let stepInterval = Math.ceil(duration / smoothLevel); // 1000 / 20 = 50
            let restSize = scrollSize;
            let scrollStepSize = Math.ceil(scrollSize / smoothLevel); // 100 / 50 = 2 (0.5 rest)

            let scrollStepFn = () => {
                scrollSize = restSize > scrollStepSize ? scrollStepSize : restSize;
                switch (direction) {
                    case 'left':
                        container.scrollLeft -= scrollSize;
                        break;
                    case 'right':
                        container.scrollLeft += scrollSize;
                        break;
                    case 'up':
                        container.scrollTop -= scrollSize;
                        break;
                    case 'down':
                        container.scrollTop += scrollSize;
                        break;
                }
                restSize -= scrollSize;
                if (0 < restSize) {
                    DOM.setTimeout(scrollStepFn, stepInterval, null);
                } else {
                    res();
                }
            };
            scrollStepFn();
            // DOM.setTimeout(scrollStepFn, stepInterval);
        });

    }

    toLowerCaseKeys(obj: object): object {
        return Object.keys(obj).reduce((accum, key) => {
            accum[key.toLowerCase()] = obj[key];
            return accum;
        }, {});
    }

    removeEmptyPropertiesfromObject(obj) {
        if(typeof(obj) !== 'object') {
            return obj;
        }

        Object.keys(obj).forEach((prop)=> {
            if(this.isNullOrUndefined(obj[prop])) {
                delete obj[prop];
            }
        });

        return obj;
    }

    /**
     * @description  Remove the localhost
     */
    removeLocalhost(url){
        try {
            return url.replace(/^[a-z]{4,5}:\/{2}[a-z]{1,}:[0-9]{1,4}.(.*)/, '$1');
        }catch (e) {
            return url;
        }
    }

    capitalizeFirstLetter(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }


    lowerFirstLetter(str: string): string {
        return str.charAt(0).toLowerCase() + str.slice(1);
    }


    arraysAreEqual(arr1: any[], arr2: any[], areItemsEqual?: (item1: any, item2: any) => boolean): boolean {
        arr1 = arr1 || [];
        arr2 = arr2 || [];

        if(arr1.length !== arr2.length) {
            return false;
        }

        for(let i = 0; i < arr1.length; i++) {
            if(this.isNullOrUndefined(areItemsEqual)) {
                if(arr1[i] !== arr2[i]) {
                    return false;
                }
            } else {
                if(!areItemsEqual(arr1[i], arr2[i])) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * should return the number as bytes notation.
     * for example:
     * formatNumberToBytesNotation(1000, 0) will return 1K
     * formatNumberToBytesNotation(3000000, 0) will return 3M
     * formatNumberToBytesNotation(1500, 1) will return 1.5K
     * @param num
     * @param digits
     */
    formatNumberToBytesNotation(num: number, digits: number) {
        const formatMap = [
            { value: 1, symbol: "" },
            { value: 1E3, symbol: "K" },
            { value: 1E6, symbol: "M" },
            { value: 1E9, symbol: "G" },
            { value: 1E12, symbol: "T" },
            { value: 1E15, symbol: "P" },
            { value: 1E18, symbol: "E" }
        ];
        const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
        let index;
        for (index = formatMap.length - 1; index > 0; index--) {
            if (num >= formatMap[index].value) {
                break;
            }
        }
        return (num / formatMap[index].value).toFixed(digits).replace(rx, "$1") + formatMap[index].symbol;
    }

     once(fn, context = this) {
        let result;
        return function() {
            if (fn) {
                result = fn.apply(context || this, arguments);
                fn = null;
            }
            return result;
        };
    }
}

export default new Underscore();